import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./support-chat.css";
import "./support-profile.css";

type ChatMessage = { id?: string; role: "user" | "assistant"; content: string };
type CustomerProfile = { name: string; email: string; phone: string };
type ChatSlot = { id: string; label: string };

const welcomeMessage: ChatMessage = {
  id: "welcome",
  role: "assistant",
  content: "Hi! I’m Visiosol’s virtual assistant. Ask me about our services or FBR Connect.",
};

function getSessionId() {
  const key = "visiosol_support_session";
  let value = window.localStorage.getItem(key);
  if (!value) {
    value = window.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(key, value);
  }
  return value;
}

export default function SupportChat() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([welcomeMessage]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [slots, setSlots] = useState<ChatSlot[]>([]);
  const [profile, setProfile] = useState<CustomerProfile>(() => {
    try { return JSON.parse(window.localStorage.getItem("visiosol_support_profile") || "null") || { name: "", email: "", phone: "" }; }
    catch { return { name: "", email: "", phone: "" }; }
  });
  const [profileReady, setProfileReady] = useState(() => Boolean(window.localStorage.getItem("visiosol_support_profile")));
  const listRef = useRef<HTMLDivElement>(null);

  const loadMessages = useCallback(async () => {
    const response = await fetch(`/api/support/messages?sessionId=${encodeURIComponent(getSessionId())}`, { cache: "no-store" });
    if (!response.ok) return;
    const result = await response.json();
    if (result.messages?.length) {
      setMessages((current) => [
        welcomeMessage,
        ...result.messages,
        ...current.filter((message) => message.id?.startsWith("pending-") || message.id?.startsWith("error-")),
      ]);
    }
  }, []);

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);
  useEffect(() => {
    if (!open) return;
    loadMessages().catch(() => undefined);
    const refresh = () => { if (document.visibilityState === "visible") loadMessages().catch(() => undefined); };
    const timer = window.setInterval(refresh, 5000);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
  }, [open, loadMessages]);

  if (pathname === "/inbox" || pathname === "/leads" || pathname === "/inbox-settings") return null;

  const sendMessage = async (message: string) => {
    if (!message || sending) return;
    setDraft("");
    setSlots([]);
    setSending(true);
    const visibleMessage = message.replace(/^\[Selected demo slot\]\s*/, "Selected demo slot: ");
    const pendingId = `pending-${Date.now()}`;
    setMessages((items) => [...items, { id: pendingId, role: "user", content: visibleMessage }]);
    try {
      const response = await fetch("/api/support/chat", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId: getSessionId(), message, profile }) });
      const result = await response.json();
      setMessages((items) => [...items.filter((item) => item.id !== pendingId), { id: response.ok ? `reply-${Date.now()}` : `error-${Date.now()}`, role: "assistant", content: response.ok ? result.reply : result.error }]);
      if (response.ok) setSlots(result.slots || []);
      if (response.ok) loadMessages().catch(() => undefined);
    } catch {
      setMessages((items) => [...items, { id: `error-${Date.now()}`, role: "assistant", content: "I’m temporarily unavailable. Please email info@visiosol.tech or WhatsApp +92 333 5274666." }]);
    } finally {
      setSending(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await sendMessage(draft.trim());
  };

  const saveProfile = (event: FormEvent) => {
    event.preventDefault();
    const cleaned = { name: profile.name.trim(), email: profile.email.trim(), phone: profile.phone.trim() };
    if (!cleaned.name || !cleaned.email || !cleaned.phone) return;
    window.localStorage.setItem("visiosol_support_profile", JSON.stringify(cleaned));
    setProfile(cleaned);
    setProfileReady(true);
  };

  return <div className={open ? "support-chat is-open" : "support-chat"}>
    {open && <section className="support-panel" aria-label="Visiosol customer support chat">
      <header><div><span className="support-dot"></span><strong>Visiosol assistant</strong><small>AI support · Human help available</small></div><button type="button" onClick={() => setOpen(false)} aria-label="Close chat">×</button></header>
      {!profileReady ? <form className="support-profile" onSubmit={saveProfile}><h3>Before we begin</h3><p>Please share your contact details so our team can follow up.</p><label>Name<input required maxLength={100} autoComplete="name" value={profile.name} onChange={(event) => setProfile({ ...profile, name: event.target.value })}/></label><label>Email<input required type="email" maxLength={160} autoComplete="email" value={profile.email} onChange={(event) => setProfile({ ...profile, email: event.target.value })}/></label><label>Phone<input required type="tel" maxLength={32} autoComplete="tel" value={profile.phone} onChange={(event) => setProfile({ ...profile, phone: event.target.value })}/></label><button type="submit">Start chat</button></form> : <>
      <div className="support-messages" ref={listRef} aria-live="polite">{messages.map((item, index) => <div key={item.id || index} className={`support-message ${item.role}`}>{item.content}</div>)}{slots.length > 0 && <div className="support-slots"><strong>Available times</strong>{slots.map((slot) => <button type="button" key={slot.id} disabled={sending} onClick={() => sendMessage(`[Selected demo slot] ${slot.label}`)}>{slot.label}</button>)}<small>Your selection is a request. We will confirm availability separately.</small></div>}{sending && <div className="support-message assistant support-typing"><i></i><i></i><i></i></div>}</div>
      <form onSubmit={submit}><textarea value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } }} maxLength={2000} placeholder="Ask about our services…" aria-label="Your message"/><button disabled={!draft.trim() || sending} aria-label="Send message">↑</button></form></>}
      <p className="support-notice">By chatting, you agree to our <Link to="/privacy-policy">privacy policy</Link>. Don’t share passwords or sensitive credentials.</p>
    </section>}
    <button className="support-launcher" type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={open ? "Close support chat" : "Open support chat"}>{open ? "×" : <><span>Chat with us</span><b>◆</b></>}</button>
  </div>;
}
